import { z } from 'zod';
import { paginationQuery } from './common.js';
/** Tipos de identificación admitidos para un receptor (v4.4 agrega 05 y 06). */
export const partyIdentificationType = z.enum(['01', '02', '03', '04', '05', '06']);
export const partyIdentificationLabel = {
    '01': 'Cédula física',
    '02': 'Cédula jurídica',
    '03': 'DIMEX',
    '04': 'NITE',
    '05': 'Extranjero no domiciliado',
    '06': 'No contribuyente',
};
const partyPatterns = {
    '01': /^[1-9]\d{8}$/,
    '02': /^\d{10}$/,
    '03': /^\d{11,12}$/,
    '04': /^\d{10}$/,
    '05': /^[A-Za-z0-9-]{3,20}$/,
    '06': /^[A-Za-z0-9-]{3,20}$/,
};
export const customerKind = z.enum(['customer', 'supplier', 'both']);
export const customerKindLabel = {
    customer: 'Cliente',
    supplier: 'Proveedor',
    both: 'Cliente y proveedor',
};
const optionalText = (max) => z
    .string()
    .trim()
    .max(max)
    .transform((v) => v || null)
    .nullable()
    .optional();
export const contactInput = z
    .object({
    id: z.uuid().optional(),
    // Una fila vacía se ignora al guardar (como en el sistema anterior); con datos, el nombre es obligatorio.
    name: z.string().trim().max(120),
    email: z
        .email('Correo inválido')
        .trim()
        .toLowerCase()
        .nullable()
        .optional()
        .or(z.literal('').transform(() => null)),
    phone: optionalText(30),
    position: optionalText(80),
    receivesDocuments: z.boolean().default(true),
})
    .superRefine((v, ctx) => {
    const filled = !!(v.email || v.phone || v.position);
    if (v.name.length < 2 && (filled || v.name.length === 1)) {
        ctx.addIssue({ code: 'custom', path: ['name'], message: 'Requerido' });
    }
});
export const customerInput = z
    .object({
    kind: customerKind.default('customer'),
    name: z.string().trim().min(2, 'Requerido').max(160),
    tradeName: optionalText(160),
    identificationType: partyIdentificationType.nullable().optional(),
    identification: optionalText(20),
    email: z
        .email('Correo inválido')
        .trim()
        .toLowerCase()
        .nullable()
        .optional()
        .or(z.literal('').transform(() => null)),
    ccEmails: z.array(z.email('Correo inválido').trim().toLowerCase()).max(10).default([]),
    phone: optionalText(30),
    provinceCode: z.string().regex(/^\d$/).nullable().optional(),
    cantonCode: z
        .string()
        .regex(/^\d{2}$/)
        .nullable()
        .optional(),
    districtCode: z
        .string()
        .regex(/^\d{2}$/)
        .nullable()
        .optional(),
    barrioCode: z
        .string()
        .regex(/^\d{2}$/)
        .nullable()
        .optional(),
    addressDetail: optionalText(250),
    economicActivity: z
        .string()
        .regex(/^\d{4,6}(\.\d)?$/, 'Código de 6 dígitos')
        .nullable()
        .optional(),
    creditDays: z.coerce.number().int().min(0).max(365).default(0),
    notes: optionalText(1000),
    contacts: z.array(contactInput).max(20).default([]),
})
    .superRefine((v, ctx) => {
    if (v.identification && !v.identificationType) {
        ctx.addIssue({
            code: 'custom',
            path: ['identificationType'],
            message: 'Elegí el tipo de identificación',
        });
    }
    if (v.identificationType && !v.identification) {
        ctx.addIssue({ code: 'custom', path: ['identification'], message: 'Requerido' });
    }
    const pattern = v.identificationType ? partyPatterns[v.identificationType] : undefined;
    if (pattern && v.identification && !pattern.test(v.identification)) {
        ctx.addIssue({
            code: 'custom',
            path: ['identification'],
            message: `Formato inválido para ${partyIdentificationLabel[v.identificationType]}`,
        });
    }
});
export const customerListQuery = paginationQuery.extend({
    kind: z.enum(['customer', 'supplier']).optional(),
    status: z.enum(['active', 'archived', 'all']).default('active'),
});
//# sourceMappingURL=customers.js.map